import { Highlight } from '@/components/HighlightText';
import { formatTimestamp, utils } from '@/helpers';
import type { Campaign, CampaignUID } from '@/types/campaigns';
import type { CampaignsType } from '@/types';
import { Flex, Icon, Td, Typography } from '@alpine-iq/ui-library';
import React from 'react';
import CampaignStatusCell from './table-cells/CampaignStatusCell';
import { CampaignChannelIcons } from './table-cells/CampaignChannelIcon';
import CampaignScheduledCell from './table-cells/CampaignScheduledCell';
import CampaignTagsRender from '@/pages/campaign-builder/components/tags/CampaignTagsRender';
import { isVariantCampaign } from '@/pages/campaign-builder/campaign.builder.utils';

const TableKey = 'campaignsTable';

type RenderProps = {
	search?: string;
};

export type CampaignColumnConfig = {
	label: string;
	sortable?: boolean;
	localKey?: string;
	enabled: () => boolean;
	setEnabled: (value: boolean) => void;
	settings: () => CampaignColumnSaveSetting;
	setSettings: (value: Partial<CampaignColumnSaveSetting>) => void;
	clearSettings: () => void;
	render: (campaign: CampaignsType, props: RenderProps) => React.ReactNode;
};

export type LegacyCampaignColumnConfig = Omit<CampaignColumnConfig, 'render'> & {
	render: (campaign: CampaignsType, props: RenderProps) => React.ReactNode;
};

export type CampaignColumnSaveSetting = {
	enabled?: boolean;
	columnOrder?: number;
};

const getLocal = (key: string, defaultValue: CampaignColumnSaveSetting) => {
	const local = utils.local.get(key, defaultValue);
	return { ...defaultValue, ...local };
};

const setLocal = (key: string, value: CampaignColumnSaveSetting) => {
	utils.local.set(key, value);
};

export type CampaignTablekeys = keyof CampaignUID | keyof Campaign | 'status' | 'channels';

export const getColumnDefaults = (
	name: CampaignTablekeys,
	isDefault?: boolean,
	columnOrder?: number,
): Omit<CampaignColumnConfig, 'label' | 'render' | 'sortable'> => {
	return {
		settings: () => getLocal(`${TableKey}.${name}`, { enabled: isDefault, columnOrder }),
		enabled: () => CampaignsTableHeaders?.[name]?.settings?.()?.enabled ?? isDefault ?? false,
		setEnabled: (value) => CampaignsTableHeaders?.[name]?.setSettings?.({ enabled: value }),
		setSettings: (value) => setLocal(`${TableKey}.${name}`, { ...getLocal(`${TableKey}.${name}`, { enabled: true }), ...value }),
		clearSettings: () => setLocal(`${TableKey}.${name}`, { enabled: isDefault }),
	};
};

export const CampaignsTableHeaders: Partial<Record<CampaignTablekeys, CampaignColumnConfig>> = {
	status: {
		sortable: true,
		label: 'Status',
		...getColumnDefaults('status', true),
		render(campaign, props) {
			return <CampaignStatusCell campaign={campaign} />;
		},
	},
	name: {
		sortable: true,
		label: 'Name',
		...getColumnDefaults('name', true),
		render(campaign, props) {
			return (
				<Td>
					<Flex align="center">
						<span style={{ width: 'max-content', maxWidth: 300 }}>
							<Highlight
								text={campaign.name || 'Untitled Campaign'}
								search={props?.search ?? ''}
							/>
						</span>

						<CampaignTagsRender
							campaignID={campaign.id}
							tags={campaign.tags}
						/>
					</Flex>
				</Td>
			);
		},
	},
	id: {
		sortable: true,
		label: 'ID',
		...getColumnDefaults('id', true),
		render(campaign, props) {
			return (
				<Td>
					<Typography>
						<Highlight
							text={`${campaign.id}`}
							search={props?.search ?? ''}
						/>
					</Typography>
				</Td>
			);
		},
	},
	lastMessage: {
		sortable: true,
		label: 'Last Message',
		...getColumnDefaults('lastMessage', true),
		render(campaign, _) {
			const lastMessage = (campaign as CampaignUID).lastMessage;
			if (!lastMessage) return <Td />;
			return (
				<Td>
					<Typography>{formatTimestamp(lastMessage, { hideTime: true })}</Typography>
					<Typography variant="body-sm">
						{formatTimestamp(lastMessage, {
							month: false,
							day: false,
							year: false,
						})}
					</Typography>
				</Td>
			);
		},
	},
	scheduled: {
		sortable: true,
		label: 'Scheduled',
		...getColumnDefaults('scheduled', true),
		render(campaign, _) {
			return (
				<Td>
					<CampaignScheduledCell campaign={campaign} />
				</Td>
			);
		},
	},
	channels: {
		sortable: true,
		label: 'Channels',
		...getColumnDefaults('channels', true),
		render(campaign, _) {
			const isCoMarketing = campaign.isCoMarketing || campaign.coMarketing?.installedFromUserID;

			return (
				<Td>
					<Flex column>
						<CampaignChannelIcons campaign={campaign} />
						{isCoMarketing && (
							<Typography
								variant="h3"
								style={{ color: 'var(--text-secondary)', fontSize: 12, lineHeight: '18px', letterSpacing: '1px' }}
							>
								CO-MARKETING
							</Typography>
						)}
						{isVariantCampaign(campaign) && (
							<Typography
								variant="h3"
								style={{ color: 'var(--text-secondary)', fontSize: 12, lineHeight: '18px', letterSpacing: '1px' }}
							>
								A/B TESTING
							</Typography>
						)}
					</Flex>
				</Td>
			);
		},
	},
	updated: {
		sortable: true,
		label: 'Last Updated',
		...getColumnDefaults('updated', false),
		render(campaign, _) {
			return (
				<Td>
					<Typography>{formatTimestamp(campaign.updated, { hideTime: true })}</Typography>
					<Typography variant="body-sm">
						{formatTimestamp(campaign.updated, {
							month: false,
							day: false,
							year: false,
						})}
					</Typography>
				</Td>
			);
		},
	},
	created: {
		sortable: true,
		label: 'Created',
		...getColumnDefaults('created', false),
		render(campaign, _) {
			return (
				<Td>
					<Typography>{formatTimestamp(campaign.created, { hideTime: true })}</Typography>
					<Typography variant="body-sm">
						{formatTimestamp(campaign.created, {
							month: false,
							day: false,
							year: false,
						})}
					</Typography>
				</Td>
			);
		},
	},
} as const;
